import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BACKEND_URL, API } from "../lib/api";

const DiscordContext = createContext(null);

// Use a dedicated axios instance with credentials for auth flow.
const authClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function DiscordProvider({ children }) {
  const [user, setUser] = useState(null);
  const [guildRequired, setGuildRequired] = useState(true);
  const [verifyRequired, setVerifyRequired] = useState(false);
  const [verifyUrl, setVerifyUrl] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await authClient.get("/auth/me");
      setGuildRequired(!!res.data.guild_required);
      setVerifyRequired(!!res.data.verify_required);
      setVerifyUrl(res.data.verify_url || null);
      setUser(res.data.authenticated ? res.data.user : null);
    } catch {
      setUser(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const connect = useCallback(() => {
    const next = encodeURIComponent(window.location.pathname || "/apply");
    window.location.href = `${BACKEND_URL}/api/auth/discord/login?next=${next}`;
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await authClient.post("/auth/logout");
    } catch (err) {
      console.warn("Discord logout failed:", err);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loaded,
      guildRequired,
      verifyRequired,
      verifyUrl,
      isConnected: !!user,
      inGuild: !!(user && user.in_guild),
      isVerified: !!(user && user.is_verified),
      isSurvivor: !!(user && user.is_survivor),
      canViewSurvivorContent: !!(user && user.can_view_survivor_content),
      isAdmin: !!(user && user.is_admin),
      isSuperAdmin: !!(user && user.is_super_admin),
      permissions: (user && Array.isArray(user.permissions)) ? user.permissions : [],
      connect,
      disconnect,
      refresh,
    }),
    [user, loaded, guildRequired, verifyRequired, verifyUrl, connect, disconnect, refresh]
  );

  return <DiscordContext.Provider value={value}>{children}</DiscordContext.Provider>;
}

export function useDiscord() {
  const ctx = useContext(DiscordContext);
  if (!ctx) throw new Error("useDiscord must be used within DiscordProvider");
  return ctx;
}
